import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseTradeDialog, { type CloseTradeData, type CloseReasonOption, SHARES_REASONS } from './CloseTradeDialog';

interface PositionsListProps {
  trades: any[];
  title?: string;
  closeReasons?: CloseReasonOption[];
  variant?: 'compact' | 'table';
  onClose?: (tradeId: number, data: CloseTradeData) => Promise<void>;
  onDelete?: (tradeId: number) => Promise<void>;
}

const strategyLabel = (s: string) =>
  s?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || '';

const legsSummary = (legs: any[]) => {
  if (!legs || legs.length === 0) return '';
  return legs.map((l: any) => {
    const prefix = l.side === 'buy' ? '+' : '-';
    const type = l.option_type === 'C' ? 'C' : 'P';
    return `${prefix}$${l.strike}${type}`;
  }).join(' / ');
};

const PositionsList: React.FC<PositionsListProps> = ({ trades, title = 'Positions', variant = 'compact', closeReasons, onClose, onDelete }) => {
  const [closeTarget, setCloseTarget] = useState<any | null>(null);

  if (!trades || trades.length === 0) return null;

  const handleCloseSubmit = async (data: CloseTradeData) => {
    if (!closeTarget || !onClose) return;
    await onClose(closeTarget.id, data);
    setCloseTarget(null);
  };

  return (
    <>
      {variant === 'table' ? (
        <Box sx={{ mb: 2 }}>
          {title && <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Strategy</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Legs</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Entry</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Opened</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Expiry</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Live Price</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">P&L</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.map((t: any) => {
                  const pnl = t.realized_pnl != null ? parseFloat(t.realized_pnl) : null;
                  const isShares = t.strategy_name === 'assigned_shares';
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell>{isShares ? 'Assigned Shares' : strategyLabel(t.strategy_name)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {isShares ? '-' : legsSummary(t.legs)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {isShares ? `${t.contracts} shares` : `${t.contracts} ct`}
                      </TableCell>
                      <TableCell align="right">${(parseFloat(t.entry_price) || 0).toFixed(2)}</TableCell>
                      <TableCell>{t.entry_date}</TableCell>
                      <TableCell>{isShares ? '-' : (t.legs?.[0]?.expiry || '-')}</TableCell>
                      <TableCell align="right">
                        {t.live_price != null ? `$${t.live_price.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={t.status} size="small" color={t.status === 'open' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          if (pnl != null) return (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: pnl >= 0 ? 'success.main' : 'error.main' }}>
                              {pnl >= 0 ? '+' : ''}${pnl.toFixed(0)}
                            </Typography>
                          );
                          // Unrealized P&L for open trades with live price
                          if (t.status === 'open' && t.live_price != null && t.entry_price != null) {
                            const entry = Math.round(parseFloat(t.entry_price) * 100) / 100;
                            const live = Math.round(t.live_price * 100) / 100;
                            const isShares = t.strategy_name === 'assigned_shares';
                            const isCredit = t.trade_direction === 'credit';
                            const multiplier = isShares ? t.contracts : t.contracts * 100;
                            const diff = isShares ? (live - entry) : (isCredit ? (entry - live) : (live - entry));
                            const unrealized = Math.round(diff * multiplier * 100) / 100;
                            return (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: unrealized >= 0 ? 'success.main' : 'error.main' }}>
                                {unrealized >= 0 ? '+' : ''}${unrealized.toFixed(0)}
                              </Typography>
                            );
                          }
                          return '-';
                        })()}
                      </TableCell>
                      <TableCell>
                        {t.status === 'open' && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {onClose && (
                              <Button size="small" variant="outlined" sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }}
                                onClick={() => setCloseTarget(t)}>
                                Close
                              </Button>
                            )}
                            {onDelete && (
                              <Button size="small" color="error" sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }}
                                onClick={() => onDelete(t.id)}>
                                Delete
                              </Button>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
          {trades.map((t: any) => {
            const legsStr = legsSummary(t.legs || []);
            return (
              <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                  {strategyLabel(t.strategy_name)}
                </Typography>
                {legsStr && (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {legsStr}
                  </Typography>
                )}
                <Typography variant="body2">{t.contracts} ct @ ${(parseFloat(t.entry_price) || 0).toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  exp {t.legs?.[0]?.expiry || '-'}
                </Typography>
                <Chip label={t.status} size="small" color={t.status === 'open' ? 'success' : 'default'} />
                {t.realized_pnl != null && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: parseFloat(t.realized_pnl) >= 0 ? 'success.main' : 'error.main' }}>
                    {parseFloat(t.realized_pnl) >= 0 ? '+' : ''}${parseFloat(t.realized_pnl).toFixed(0)}
                  </Typography>
                )}
                {t.status === 'open' && (
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                    {onClose && (
                      <Button size="small" variant="outlined" sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }}
                        onClick={() => setCloseTarget(t)}>
                        Close
                      </Button>
                    )}
                    {onDelete && (
                      <Button size="small" color="error" sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }}
                        onClick={() => onDelete(t.id)}>
                        Delete
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      {closeTarget && onClose && (() => {
        const isShares = closeTarget.strategy_name === 'assigned_shares';
        return (
        <CloseTradeDialog
          open={!!closeTarget}
          ticker={closeTarget.ticker}
          strategyName={isShares ? 'Assigned Shares' : strategyLabel(closeTarget.strategy_name)}
          legs={closeTarget.legs || []}
          totalContracts={closeTarget.contracts}
          entryPrice={parseFloat(closeTarget.entry_price)}
          closeReasons={isShares ? SHARES_REASONS : closeReasons}
          tradeDirection={isShares ? 'debit' : closeTarget.trade_direction}
          qtyLabel={isShares ? 'Shares to close' : undefined}
          onClose={() => setCloseTarget(null)}
          onSubmit={handleCloseSubmit}
        />);
      })(
      )}
    </>
  );
};

export default PositionsList;
